import type { Auth } from "firebase/auth";
import { Tooltip } from "radix-ui";
import { useEffect, useState } from "react";
import { Outlet } from "react-router";
import { Toaster } from "sonner";
import { AuthProvider } from "../auth/AuthProvider";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/Button";
import { Spinner } from "../components/Spinner";
import { loadPublicConfig } from "../lib/config";
import { initFirebaseAuth } from "../lib/firebase";
import type { PublicConfig } from "../lib/types";
import { registerServiceWorker } from "../pwa";

let swRegistered = false;

export function Splash({ label = "Opening Supperkeep" }: { label?: string }) {
  return (
    <div className="splash">
      <BrandMark />
      <Spinner size="lg" label={label} />
    </div>
  );
}

/** Root: public config → Firebase Auth → providers shared by every route. */
export function Root() {
  const [state, setState] = useState<{ config: PublicConfig; auth: Auth } | { error: true } | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadPublicConfig()
      .then((config) => {
        if (!cancelled) setState({ config, auth: initFirebaseAuth(config) });
      })
      .catch(() => {
        if (!cancelled) setState({ error: true });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  useEffect(() => {
    if (!swRegistered) {
      swRegistered = true;
      registerServiceWorker();
    }
  }, []);

  if (!state) return <Splash />;
  if ("error" in state) {
    return (
      <main className="splash" id="main">
        <BrandMark />
        <h1 className="auth-title">Can't reach Supperkeep</h1>
        <p className="muted" style={{ maxWidth: "40ch", textAlign: "center" }}>
          Check your connection. Once you've opened Supperkeep online, it can start without a connection.
        </p>
        <Button emphasis="solid" intent="brand" onClick={() => { setState(null); setAttempt((n) => n + 1); }}>
          Try again
        </Button>
      </main>
    );
  }

  return (
    <AuthProvider config={state.config} auth={state.auth}>
      <Tooltip.Provider delayDuration={300} skipDelayDuration={200}>
        <Outlet />
        <Toaster position="bottom-center" closeButton visibleToasts={4} offset={{ bottom: 88 }} mobileOffset={{ bottom: 84 }} toastOptions={{ duration: 5000 }} />
      </Tooltip.Provider>
    </AuthProvider>
  );
}
